import { useConcatPages } from "../../../../shared/infrastructure";
import { useFetch } from "../../../../public/auth/infrastructure/store/fetch-context";
import {
  LocationT,
  UseGetLocationsFilters,
  UseGetZip3MarketsFilters,
  Zip3MarketsT,
} from "../../domain";
import {
  PageAttr,
  StatusErrorResponse,
} from "../../../../shared/domain/entities/fetch-entities";
import {
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  UseMutationResult,
  useInfiniteQuery,
  useMutation,
  useQuery,
  UseQueryResult,
  UseQueryOptions,
} from "@tanstack/react-query";

const locationKeys = {
  all: ["locations"] as const,
  locations: () => [...locationKeys.all, "locations-list"] as const,
  locationsList: (filters: UseGetLocationsFilters) =>
    [...locationKeys.locations(), filters] as const,
  allZip3Markets: ["zip3-markets"] as const,
  coordinatesDistance: (filters: LocationDistancePayload) =>
    [...locationKeys.locations(), filters] as const,
  zip3Markets: () =>
    [...locationKeys.allZip3Markets, "zip3-markets-list"] as const,
  zip3MarketsList: (filters: UseGetZip3MarketsFilters) =>
    [...locationKeys.zip3Markets(), filters] as const,
};

const LOCATIONS_ENDPOINT = "locations";

const useInfiniteGetLocations = (
  filters: UseGetLocationsFilters,
  config: UseInfiniteQueryOptions<PageAttr<LocationT>> = {}
): UseInfiniteQueryResult<PageAttr<LocationT>> & {
  allPages: PageAttr<LocationT>["data"];
} => {
  const fetch = useFetch();

  const response = useInfiniteQuery<PageAttr<LocationT>>(
    locationKeys.locationsList(filters),
    async ({ pageParam: page = 1 }: { pageParam?: number }) => {
      const response = await fetch.get<PageAttr<LocationT>>(
        LOCATIONS_ENDPOINT,
        {
          params: {
            page,
            limit: 10,
            ...filters,
          },
        }
      );

      return response.data;
    },
    {
      ...config,
      getNextPageParam: (lastPage, allPages) =>
        lastPage.data.length > 0 ? allPages.length + 1 : undefined,
    }
  );

  const allPages = useConcatPages(response.data?.pages ?? []);

  return {
    ...response,
    allPages,
  };
};

type ZipCodeLocationResponse = {
  data: LocationT;
};

type ZipCodeLocationPayload = {
  locationId: LocationT["location_id"];
  payload: { zip_code: string };
};

const useZipCodeLocation = (): UseMutationResult<
  ZipCodeLocationResponse,
  StatusErrorResponse,
  ZipCodeLocationPayload
> => {
  const fetch = useFetch();

  return useMutation<
    ZipCodeLocationResponse,
    StatusErrorResponse,
    ZipCodeLocationPayload
  >(
    async (params) =>
      (
        await fetch.patch<ZipCodeLocationResponse>(
          `${LOCATIONS_ENDPOINT}/${params.locationId}`,
          params.payload
        )
      ).data
  );
};

const useInfiniteGetZip3Markets = (
  filters: UseGetZip3MarketsFilters,
  config: UseInfiniteQueryOptions<PageAttr<Zip3MarketsT>> = {}
): UseInfiniteQueryResult<PageAttr<Zip3MarketsT>> & {
  allPages: PageAttr<Zip3MarketsT>["data"];
} => {
  const fetch = useFetch();

  const response = useInfiniteQuery<PageAttr<Zip3MarketsT>>(
    locationKeys.zip3MarketsList(filters),
    async ({ pageParam: page = 1 }: { pageParam?: number }) => {
      const response = await fetch.get<PageAttr<Zip3MarketsT>>(
        `${LOCATIONS_ENDPOINT}/zip3-markets`,
        {
          params: {
            page,
            limit: 10,
            ...filters,
          },
        }
      );

      return response.data;
    },
    {
      ...config,
      getNextPageParam: (lastPage, allPages) =>
        lastPage.data.length > 0 ? allPages.length + 1 : undefined,
    }
  );

  const allPages = useConcatPages(response.data?.pages ?? []);

  return {
    ...response,
    allPages,
  };
};

type LocationDistanceResponse = {
  data: number;
};

type LocationDistancePayload = {
  coordinates: Array<string>;
};

const useLocationDistance = (
  filters: LocationDistancePayload,
  config?: UseQueryOptions<LocationDistanceResponse>
): UseQueryResult<LocationDistanceResponse> => {
  const fetch = useFetch();

  return useQuery<LocationDistanceResponse>(
    locationKeys.coordinatesDistance(filters),
    async () =>
      (
        await fetch.get<LocationDistanceResponse>(
          `${LOCATIONS_ENDPOINT}/distance`,
          { params: filters }
        )
      ).data,
    config
  );
};

export {
  locationKeys,
  useInfiniteGetLocations,
  useZipCodeLocation,
  useInfiniteGetZip3Markets,
  useLocationDistance,
};
